import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { useLanguage } from '../../lib/LanguageContext';

export default function AdminSecurity() {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [logins, setLogins] = useState([]);
  const [otps, setOtps] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [settings, setSettings] = useState(null);

  const load = async () => {
    const [s, l, o, b, st] = await Promise.all([
      api.get('/admin/security/otp-stats'),
      api.get('/admin/security/login-logs'),
      api.get('/admin/security/otp-logs'),
      api.get('/admin/security/blocked'),
      api.get('/admin/security/otp-settings'),
    ]);
    setStats(s.data); setLogins(l.data); setOtps(o.data); setBlocked(b.data); setSettings(st.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = {
      validity_minutes: parseInt(settings.validity_minutes, 10),
      max_attempts: parseInt(settings.max_attempts, 10),
      cooldown_seconds: parseInt(settings.cooldown_seconds, 10),
      max_sends_per_hour: parseInt(settings.max_sends_per_hour, 10),
      block_duration_minutes: parseInt(settings.block_duration_minutes, 10),
      enabled: settings.enabled,
    };
    await api.put('/admin/security/otp-settings', body);
    toast.success(t('common.operationSuccess'));
  };
  const unblock = async (uid) => { await api.post(`/admin/clients/${uid}/unblock`); toast.success(t('common.operationSuccess')); load(); };

  if (!stats) return <div>{t('common.loading')}</div>;

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold">{t('admin.securityTitle')}</h1>

      <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="stat-card"><div className="text-xs text-slate-500 uppercase">OTP envoyés</div><div className="text-2xl font-bold">{stats.sent}</div></div>
        <div className="stat-card"><div className="text-xs text-slate-500 uppercase">Validés</div><div className="text-2xl font-bold text-emerald-600">{stats.valid}</div></div>
        <div className="stat-card"><div className="text-xs text-slate-500 uppercase">Expirés</div><div className="text-2xl font-bold text-amber-600">{stats.expired}</div></div>
        <div className="stat-card"><div className="text-xs text-slate-500 uppercase">Invalides</div><div className="text-2xl font-bold text-rose-600">{stats.invalid}</div></div>
        <div className="stat-card"><div className="text-xs text-slate-500 uppercase">Taux de succès</div><div className="text-2xl font-bold">{stats.success_rate}%</div></div>
      </div>

      {settings && (
        <div className="bg-white rounded-2xl border p-5">
          <h2 className="font-semibold mb-3">Paramètres OTP</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div><Label>Validité (minutes)</Label><Input type="number" value={settings.validity_minutes} onChange={(e) => setSettings({ ...settings, validity_minutes: e.target.value })}/></div>
            <div><Label>Tentatives max</Label><Input type="number" value={settings.max_attempts} onChange={(e) => setSettings({ ...settings, max_attempts: e.target.value })}/></div>
            <div><Label>Cooldown envoi (s)</Label><Input type="number" value={settings.cooldown_seconds} onChange={(e) => setSettings({ ...settings, cooldown_seconds: e.target.value })}/></div>
            <div><Label>Envois max / heure</Label><Input type="number" value={settings.max_sends_per_hour} onChange={(e) => setSettings({ ...settings, max_sends_per_hour: e.target.value })}/></div>
            <div><Label>Blocage (minutes)</Label><Input type="number" value={settings.block_duration_minutes} onChange={(e) => setSettings({ ...settings, block_duration_minutes: e.target.value })}/></div>
            <div className="flex items-end gap-3"><Switch checked={settings.enabled !== false} onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}/> <span className="text-sm">OTP activé</span></div>
          </div>
          <Button onClick={save} className="mt-4 bg-blue-700" data-testid="btn-save-otp-settings">Enregistrer</Button>
        </div>
      )}

      <div className="bg-white rounded-2xl border p-5">
        <h2 className="font-semibold mb-3">Comptes bloqués</h2>
        <div className="divide-y">
          {blocked.map((u) => (
            <div key={u.id} className="py-2 flex justify-between text-sm">
              <div><div className="font-medium">{u.prenom} {u.nom}</div><div className="text-xs text-slate-500">{u.email} • jusqu'à {u.bloque_jusqu_a}</div></div>
              <Button size="sm" onClick={() => unblock(u.id)}>Débloquer</Button>
            </div>
          ))}
          {blocked.length === 0 && <div className="text-slate-500 text-sm">Aucun compte bloqué</div>}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border p-5">
          <h2 className="font-semibold mb-3">Logs de connexion</h2>
          <div className="text-xs space-y-1 max-h-72 overflow-y-auto scrollbar-thin">
            {logins.slice(0, 30).map((l) => (
              <div key={l.id} className="flex justify-between gap-3 py-1 border-b">
                <span>{new Date(l.created_at).toLocaleString('fr-FR')} • {l.email}</span>
                <Badge variant={l.statut === 'succes' ? 'default' : 'destructive'}>{l.statut}</Badge>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border p-5">
          <h2 className="font-semibold mb-3">Logs OTP</h2>
          <div className="text-xs space-y-1 max-h-72 overflow-y-auto scrollbar-thin">
            {otps.slice(0, 30).map((o, i) => (
              <div key={i} className="flex justify-between gap-3 py-1 border-b">
                <span>{new Date(o.created_at).toLocaleString('fr-FR')} • {o.code}</span>
                <Badge variant={o.status === 'valide' ? 'default' : 'secondary'}>{o.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
